import { ArrowGo, EmoCart, EmoNeutral, Filter, Plus, Zoom } from "@assets"
import { Head, router } from "@inertiajs/react"
import React, { useEffect, useState } from "react"

import FeedItem, {
  type FeedItem as FeedItemData,
} from "@/components/feed/feed-item"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/app-layout"
import {
  marketplacePath,
  marketplaceProductsPath,
  productsNewPath,
} from "@/routes"

interface Product {
  id: number
  name: string
  description?: string
  price: number
  sale_price?: number
  effective_price?: number
  stock_quantity: number
  condition: string
  created_at: string
  business: {
    id: number
    name: string
    verified: boolean
  }
  category: {
    id: number
    name: string
  }
  neighborhood?: {
    id: number
    name: string
    slug: string
  }
  reviews?: {
    id: number
    rating: number
    comment?: string
  }[]
  images?: {
    id: number
    url: string
  }[]
}

interface Props {
  products: Product[]
  categories: {
    id: number
    name: string
    slug: string
  }[]
  filters: {
    search?: string
    category_id?: number
    condition?: string
    sort?: string
    price_min?: string
    price_max?: string
  }
  can_create_product?: boolean
  pagination?: {
    current_offset: number
    limit: number
    total: number
    has_more: boolean
  }
}

const conditionOptions = [
  { value: "new", label: "🆕 Nuevo" },
  { value: "like_new", label: "✨ Como nuevo" },
  { value: "good", label: "👍 Buen estado" },
  { value: "fair", label: "📦 Estado regular" },
  { value: "poor", label: "🚮 Estado deficiente" },
]

// Emojis por slug de categoría (ver seeds.rb product_categories_data)
const categoryEmojis: Record<string, string> = {
  comida: "🍽️",
  bebidas: "🍷",
  "ropa-accesorios": "👕",
  "hogar-decoracion": "🏠",
  "libros-musica": "📚",
  "tecnologia-electronica": "📱",
  "deportes-outdoor": "⚽",
  "belleza-cuidado-personal": "💄",
  "juguetes-ninos": "🧸",
  servicios: "🔧",
}

export default function MarketplaceProducts({
  products,
  categories,
  filters,
  pagination,
  can_create_product,
}: Props) {
  const [searchTerm, setSearchTerm] = useState(filters.search ?? "")
  const [previousSearchTerm, setPreviousSearchTerm] = useState<string>("")
  const [showFilters, setShowFilters] = useState(false)
  const [priceMin, setPriceMin] = useState(filters.price_min ?? "")
  const [priceMax, setPriceMax] = useState(filters.price_max ?? "")
  const [selectedCategory, setSelectedCategory] = useState(
    filters.category_id?.toString() ?? "all",
  )
  const [selectedCondition, setSelectedCondition] = useState(
    filters.condition ?? "all",
  )
  const [selectedSort, setSelectedSort] = useState(filters.sort ?? "default")
  const [allProducts, setAllProducts] = useState(products)
  const [isLoading, setIsLoading] = useState(false)

  // Sync local state with filters from server
  React.useEffect(() => {
    setSearchTerm(filters.search ?? "")
    setPriceMin(filters.price_min ?? "")
    setPriceMax(filters.price_max ?? "")
    setSelectedCategory(filters.category_id?.toString() ?? "all")
    setSelectedCondition(filters.condition ?? "all")
    setSelectedSort(filters.sort ?? "default")
  }, [
    filters.search,
    filters.price_min,
    filters.price_max,
    filters.category_id,
    filters.condition,
    filters.sort,
  ])

  useEffect(() => {
    if (products.length > 0) {
      setAllProducts((prevProducts) => {
        const urlParams = new URLSearchParams(window.location.search)
        const currentOffset = parseInt(urlParams.get("offset") ?? "0")
        const isLoadMore = currentOffset > 0

        if (!isLoadMore) {
          return products
        } else {
          const newProducts = products.filter(
            (newProduct) =>
              !prevProducts.some(
                (existingProduct) => existingProduct.id === newProduct.id,
              ),
          )
          return [...prevProducts, ...newProducts]
        }
      })
      setIsLoading(false)
    } else if (products.length === 0 && !isLoading) {
      setAllProducts([])
    }
  }, [products, isLoading])

  useEffect(() => {
    if (
      previousSearchTerm.trim() &&
      !searchTerm.trim() &&
      previousSearchTerm !== searchTerm
    ) {
      handleSearch()
    }
    setPreviousSearchTerm(searchTerm)
  }, [searchTerm, previousSearchTerm])

  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    setIsLoading(false)
    router.get(
      marketplaceProductsPath(),
      {
        ...filters,
        search: searchTerm ?? undefined,
        offset: 0,
      },
      { preserveState: true },
    )
  }

  const handleFilterChange = (key: string, value: string) => {
    const cleanValue =
      value === "all" || value === "default" ? undefined : value
    setIsLoading(false)
    router.get(
      marketplaceProductsPath(),
      {
        ...filters,
        [key]: cleanValue,
        offset: 0,
      },
      { preserveState: true },
    )
  }

  const handleLoadMore = () => {
    if (isLoading || !pagination?.has_more) return

    setIsLoading(true)
    const nextOffset = pagination.current_offset + pagination.limit

    router.get(
      marketplaceProductsPath(),
      {
        ...filters,
        search: searchTerm || undefined,
        offset: nextOffset,
      },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["products", "pagination"],
        onFinish: () => setIsLoading(false),
      },
    )
  }

  return (
    <AppLayout>
      <Head title="Productos" />
      <div className="mx-auto max-w-6xl space-y-[15px] md:p-6">
        <div className="flex flex-col">
          <div className="flex items-center justify-center gap-4">
            <h1 className="text-foreground flex items-center gap-2 text-[21px] font-bold tracking-[0.21px] md:text-3xl">
              <span className="bg-card flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full p-1">
                <img
                  src={EmoCart}
                  alt="Cart"
                  className="h-full w-full object-contain"
                />
              </span>
              Market
            </h1>

            {can_create_product && (
              <Button
                variant="default"
                onClick={() => router.visit(productsNewPath())}
                className="flex flex-shrink-0 items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Vender producto</span>
              </Button>
            )}
          </div>

          {/* Descripción debajo del título */}
          <p className="mx-10 mt-[8px] text-center text-[13px] font-normal tracking-[0.2px] text-balance">
            Compra y vende productos en la comunidad argentina
          </p>
        </div>

        {/* Tab Navigation */}
        <Tabs value="products" className="">
          <div className="flex justify-center">
            <TabsList className="bg-foreground inline-flex h-auto rounded-[15px] px-[10px] py-[10px] tracking-[0.26px]">
              <TabsTrigger
                value="businesses"
                className="data-[state=active]:bg-card flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:font-bold"
                onClick={() => router.visit(marketplacePath())}
              >
                Negocios
              </TabsTrigger>
              <TabsTrigger
                value="products"
                className="data-[state=active]:bg-card flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:font-bold"
              >
                Productos
              </TabsTrigger>
            </TabsList>
          </div>
        </Tabs>

        {/* Search and Filters Section */}
        <div className="mx-[12px]">
          <div className="flex w-full items-center gap-[8px]">
            <form
              onSubmit={handleSearch}
              className="bg-card w-full rounded-[12px]"
            >
              <div className="relative flex items-end">
                <Zoom className="text-primary bg-card mx-[10px] my-[6px] h-[34px] fill-current" />
                <Input
                  placeholder="Buscar productos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="placeholder:text-muted-foreground w-full border-0 px-0 pt-0 pb-[6px] text-[15px] font-light tracking-[0.07px] opacity-[0.75] shadow-[0_0_0_0px_rgba(0,0,0,0)] focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <Button
                  type="submit"
                  size="icon"
                  className="text-primary bg-card m-[6px] h-[34px] w-[34px] rounded-[8px] shadow-[0px_0px_4px_#00000029]"
                >
                  <ArrowGo className="text-primary size-6 fill-current" />
                </Button>
              </div>
            </form>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              className={`bg-card h-[34px] w-[34px] rounded-[8px] border-0 shadow-[0px_0px_4px_#00000029] ${
                showFilters && "h-[34px] w-[34px] shadow-md"
              }`}
            >
              <Filter className="text-primary size-6 fill-current" />
            </Button>
          </div>

          {/* Filters Section */}
          {showFilters && (
            <Card className="relative mt-2 border-0 px-3 py-[15px] shadow-none">
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                className="text-primary absolute top-3 right-3 flex h-[17px] w-[17px] items-center justify-center rounded-full bg-transparent text-[17px] leading-none"
              >
                ×
              </button>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                {/* Category and Condition Row */}
                <div className="grid grid-cols-2 gap-3 md:grid-cols-2">
                  {/* Category Filter */}
                  <div className="space-y-3">
                    <label className="text-foreground text-[12px] font-semibold tracking-[0.24px]">
                      Categoría
                    </label>
                    <Select
                      value={selectedCategory}
                      onValueChange={(value) => {
                        setSelectedCategory(value)
                        handleFilterChange("category_id", value)
                      }}
                    >
                      <SelectTrigger className="bg-popover focus:border-primary h-[40px] w-full rounded-[9px] transition-colors">
                        <SelectValue placeholder="Seleccionar categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          value="all"
                          className="focus:bg-background focus:text-primary font-medium"
                        >
                          📦 Todas
                        </SelectItem>
                        {categories.map((category) => (
                          <SelectItem
                            key={category.id}
                            value={category.id.toString()}
                            className="focus:bg-background focus:text-primary font-medium"
                          >
                            {categoryEmojis[category.slug] || "🛍️"}{" "}
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Condition Filter */}
                  <div className="space-y-3">
                    <label className="text-foreground text-[12px] font-semibold tracking-[0.24px]">
                      Estado
                    </label>
                    <Select
                      value={selectedCondition}
                      onValueChange={(value) => {
                        setSelectedCondition(value)
                        handleFilterChange("condition", value)
                      }}
                    >
                      <SelectTrigger className="bg-popover focus:border-primary h-[40px] w-full rounded-[9px] transition-colors">
                        <SelectValue placeholder="Seleccionar estado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          value="all"
                          className="focus:bg-background focus:text-primary font-medium"
                        >
                          📦 Todos
                        </SelectItem>
                        {conditionOptions.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            className="focus:bg-background focus:text-primary font-medium"
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Price Range Filter */}
                <div className="space-y-3">
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="text-foreground text-[12px] font-semibold tracking-[0.24px]">
                        Precio mínimo
                      </label>
                      <div className="relative">
                        <span className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2 transform">
                          $
                        </span>
                        <Input
                          type="number"
                          placeholder="0"
                          value={priceMin}
                          onChange={(e) => {
                            setPriceMin(e.target.value)
                            handleFilterChange("price_min", e.target.value)
                          }}
                          className="bg-popover focus:border-primary h-10 rounded-[9px] pl-8 transition-colors"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="text-foreground text-[12px] font-semibold tracking-[0.24px]">
                        Precio máximo
                      </label>
                      <div className="relative">
                        <span className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2 transform">
                          $
                        </span>
                        <Input
                          type="number"
                          placeholder="∞"
                          value={priceMax}
                          onChange={(e) => {
                            setPriceMax(e.target.value)
                            handleFilterChange("price_max", e.target.value)
                          }}
                          className="bg-popover hover:border-primary/50 focus:border-primary h-10 rounded-[9px] pl-8 transition-colors"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sort Filter */}
                <div className="space-y-3">
                  <label className="text-foreground text-[12px] font-semibold tracking-[0.24px]">
                    Ordenado por
                  </label>
                  <Select
                    value={selectedSort}
                    onValueChange={(value) => {
                      setSelectedSort(value)
                      handleFilterChange("sort", value)
                    }}
                  >
                    <SelectTrigger className="bg-popover hover:border-primary/50 focus:border-primary h-10 w-full rounded-[9px] transition-colors">
                      <SelectValue placeholder="Seleccionar orden" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        value="default"
                        className="focus:bg-background focus:text-primary font-medium"
                      >
                        📅 Más recientes primero
                      </SelectItem>
                      <SelectItem
                        value="price_asc"
                        className="focus:bg-background focus:text-primary font-medium"
                      >
                        Precio: Menor a Mayor
                      </SelectItem>
                      <SelectItem
                        value="price_desc"
                        className="focus:bg-background focus:text-primary font-medium"
                      >
                        Precio: Mayor a Menor
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Products Grid */}
        {allProducts.length > 0 ? (
          <div className="grid grid-cols-2 gap-x-[12px] gap-y-[18px] px-[12px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {allProducts.map((p) => (
              <FeedItem
                key={p.id}
                variant="grid"
                item={
                  {
                    type: "product",
                    id: p.id,
                    created_at: p.created_at,
                    name: p.name,
                    price: p.price,
                    sale_price: p.sale_price,
                    current_price: p.effective_price ?? p.sale_price ?? p.price,
                    condition: p.condition,
                    business: {
                      id: p.business.id,
                      name: p.business.name,
                      verified: p.business.verified,
                    },
                    images: p.images?.map((img) => img.url),
                  } as FeedItemData
                }
              />
            ))}
          </div>
        ) : (
          <div
            className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
            style={{ background: "var(--gradient-highlight)" }}
          >
            <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
              <img src={EmoNeutral} alt="Sin contenido" />
            </div>

            <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
              No se encontraron productos
            </h3>
          </div>
        )}

        {/* Load More Button */}
        {allProducts.length > 0 && pagination?.has_more && (
          <div className="text-center">
            <Button
              variant="ghost"
              onClick={handleLoadMore}
              disabled={isLoading}
              className="text-primary hover:bg-muted"
            >
              {isLoading ? "Cargando..." : "Ver más productos"}
            </Button>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
